import React, { useState } from 'react'
import type { ComponentEntry } from '../types'
import { wideComponents } from './component-groups.config'
import { ComponentCard } from './ComponentCard'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { cn } from 'components/UI/cn'

interface TierSectionProps {
  tier: 1 | 2 | 3
  components: ComponentEntry[]
  getPreviewer: (entry: ComponentEntry) => React.ReactNode | undefined
}

const tierMeta = {
  1: {
    label: 'Tier 1 — Primitives',
    description: 'shadcn/Radix UI components',
    color: 'text-success',
  },
  2: {
    label: 'Tier 2 — Extended',
    description: 'Custom extensions of primitives',
    color: 'text-warning',
  },
  3: {
    label: 'Tier 3 — Composites',
    description: 'Domain-specific components',
    color: 'text-orange-700',
  },
}

export function TierSection({
  tier,
  components,
  getPreviewer,
}: TierSectionProps): React.ReactElement {
  const [open, setOpen] = useState(true)
  const meta = tierMeta[tier]

  return (
    <section>
      <button
        type="button"
        className="flex items-center gap-3 w-full text-left py-3 px-1 hover:bg-surface-hover rounded-md"
        onClick={() => setOpen((o) => !o)}
      >
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <span className={cn('font-semibold text-base', meta.color)}>
          {meta.label}
        </span>
        <span className="text-fg-secondary text-sm">{meta.description}</span>
        <span className="ml-auto text-xs bg-surface-alt text-fg-secondary px-2 py-0.5 rounded-full">
          {components.length}
        </span>
      </button>

      {open && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2 pb-8">
          {components.map((entry) => (
            <ComponentCard
              key={entry.path}
              entry={entry}
              preview={getPreviewer(entry)}
              wide={wideComponents.has(entry.name)}
            />
          ))}
        </div>
      )}
    </section>
  )
}
